import React, { type Dispatch, type SetStateAction, type MouseEvent } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { styled, css, jsx } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import type { LegacyData } from '@atlassian/jira-development-details-loader/src/common/types/index.tsx';
import { useDevOpsDetailsDialogLoaderActions } from '@atlassian/jira-development-details-loader/src/controllers/main.tsx';
import { REVIEW_PANEL_DATA_TYPE } from '@atlassian/jira-development-details/src/common/model/constants.tsx';
import type { PanelDataTypes } from '@atlassian/jira-development-details/src/main/model/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { useSidebarStore } from '@atlassian/jira-software-releases-issue-sidebar-use-sidebar-store/src/controllers/index.tsx';
import {
	ISSUE_CARD_CLICK_IGNORE_TARGET,
	getIssueCardClickIgnorePreventDefaultTargetSelector,
	getIssueCardClickIgnoreTargetNoPreventDefaultSelector,
} from '@atlassian/jira-software-releases-version-detail-issue-card-click-prevent-controller/src/common/constants.tsx';
import { IssueWarningProvider } from '@atlassian/jira-software-releases-version-issue-warning-controller/src/ui/index.tsx';
import {
	VIEWPORT_WIDTH_MEDIUM,
	ISSUE_CARD_DATA_COMPONENT_SELECTOR,
} from '../../../common/constants.tsx';
import { ErrorBoundary } from '../../../common/utils/non-critical-error-boundary.tsx';
import type { issueCard_softwareReleasesVersionDetailIssueList_jiraVersion$key } from './__generated__/issueCard_softwareReleasesVersionDetailIssueList_jiraVersion.graphql';
import type { issueCard_softwareReleasesVersionDetailIssueList_jiraIssue$key } from './__generated__/issueCard_softwareReleasesVersionDetailIssueList_jiraIssue.graphql';
import { Assignee } from './assignee/index.tsx';
import { Delivery, DeliveryContainer } from './delivery/index.tsx';
import { Development, DevelopmentContainer } from './development/index.tsx';
import { FeatureFlags } from './feature-flags/index.tsx';
import { MoreButton, OnHoverVisibility } from './more-button/index.tsx';
import { Priority } from './priority/index.tsx';
import { Status } from './status/index.tsx';
import { Summary } from './summary/index.tsx';

const DEV_DETAILS_DIALOG_SCOPE_ID = 'VersionDetailsIssueList';

type Props = {
	jiraIssue: issueCard_softwareReleasesVersionDetailIssueList_jiraIssue$key;
	jiraVersion: issueCard_softwareReleasesVersionDetailIssueList_jiraVersion$key;
	hasBambooDeployment: boolean;
	hasDeployments: boolean;
	hasDevelopments: boolean;
	hasBuilds: boolean;
	onFeatureFlagEmptyStateModalOpen: Dispatch<SetStateAction<boolean>>;
	featureFlagButtonRef: React.RefObject<HTMLButtonElement>;
	rowNum: number;
};

export const IssueCard = ({
	jiraIssue,
	jiraVersion,
	hasBambooDeployment,
	hasDeployments,
	hasDevelopments,
	hasBuilds,
	onFeatureFlagEmptyStateModalOpen,
	featureFlagButtonRef,
	rowNum,
}: Props): React.JSX.Element => {
	const issue = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issueCard_softwareReleasesVersionDetailIssueList_jiraIssue on JiraIssue {
				...moreButton_softwareReleasesVersionDetailIssueList_jiraIssue
				...ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue
				issueId @required(action: THROW)
				key @required(action: THROW)
				fieldsById(ids: ["priority", "summary", "assignee", "status", "issuetype"])
					@required(action: THROW) {
					edges @required(action: THROW) {
						node {
							fieldId @required(action: THROW)
							... on JiraStatusField {
								...status_softwareReleasesVersionDetailIssueList
							}
							... on JiraSingleSelectUserPickerField {
								...assignee_softwareReleasesVersionDetailIssueList
							}
							... on JiraSingleLineTextField {
								...summary_softwareReleasesVersionDetailIssueList_jiraIssueSummaryField
							}
							... on JiraIssueTypeField {
								...summary_softwareReleasesVersionDetailIssueList_jiraIssueTypeField
							}
							... on JiraPriorityField {
								...priority_softwareReleasesVersionDetailIssueList
							}
						}
					}
				}
				...delivery_softwareReleasesVersionDetailIssueList
					@skip(if: $isDevopsFeatureDisabledInFedRamp)
				...development_softwareReleasesVersionDetailIssueList
					@skip(if: $isDevopsFeatureDisabledInFedRamp)
				...featureFlags_softwareReleasesVersionDetailIssueList_jiraIssue
					@skip(if: $isDevopsFeatureDisabledInFedRamp)
				...summary_softwareReleasesVersionDetailIssueList_jiraIssue
			}
		`,
		jiraIssue,
	);

	const { showDevDetailDialog } = useDevOpsDetailsDialogLoaderActions(DEV_DETAILS_DIALOG_SCOPE_ID);
	const [, sidebarActions] = useSidebarStore();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleClick = (dataType: PanelDataTypes, reviewData?: LegacyData) => {
		showDevDetailDialog(
			dataType,
			dataType === REVIEW_PANEL_DATA_TYPE ? reviewData : undefined,
			issue.key,
			issue.issueId,
		);
	};

	const jiraVersionData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issueCard_softwareReleasesVersionDetailIssueList_jiraVersion on JiraVersion {
				...ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion
				canViewDevTools
				versionIssueTableHiddenColumns @required(action: THROW)
				devOpsSummarisedEntities @skip(if: $isDevopsFeatureDisabledInFedRamp) {
					...featureFlags_softwareReleasesVersionDetailIssueList_devOpsSummarisedEntities
				}
				...moreButton_softwareReleasesVersionDetailIssueList_jiraVersion
			}
		`,
		jiraVersion,
	);
	const { canViewDevTools, devOpsSummarisedEntities, versionIssueTableHiddenColumns } =
		jiraVersionData;
	const fields = issue.fieldsById.edges.map((edge) => edge?.node).filter(Boolean);
	const jiraIssueSummaryField = fields.find((fieldNode) => fieldNode?.fieldId === 'summary');
	const jiraIssueTypeField = fields.find((fieldNode) => fieldNode?.fieldId === 'issuetype');
	const jiraPriorityField = fields.find((fieldNode) => fieldNode?.fieldId === 'priority');
	const jiraStatusField = fields.find((fieldNode) => fieldNode?.fieldId === 'status');
	const jiraAssigneeField = fields.find((fieldNode) => fieldNode?.fieldId === 'assignee');

	const issueUrl = `/browse/${issue.key}`;
	const onIssueClick = (e: MouseEvent) => {
		if (
			e.target instanceof Element &&
			e.target.closest(getIssueCardClickIgnorePreventDefaultTargetSelector()) != null
		) {
			// These are a tags with href to create a feature flag that shouldn't prevent default
			if (e.target.closest(getIssueCardClickIgnoreTargetNoPreventDefaultSelector())) return;
			e.preventDefault();
			return;
		}

		const modifiersPressed = Boolean(e.shiftKey || e.metaKey || e.ctrlKey);
		const shouldOpenIssueSidebar =
			// on primary button click
			e.button === 0 &&
			// no modifier buttons have been pressed
			!modifiersPressed;

		if (shouldOpenIssueSidebar) {
			// stops the default behaviour of opening the issue in a new link
			e.preventDefault();
			sidebarActions.showIssue(issue.key);
		}
		const event: UIAnalyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});
		fireUIAnalytics(event, 'versionDetailsIssue', {
			clickType: e.button,
			modifiersPressed,
		});
	};

	return (
		<IssueWarningProvider jiraVersion={jiraVersionData} jiraIssue={issue}>
			{({
				hasWarnings,
				hasOpenPullRequestWarning,
				hasFailingBuildsWarning,
				hasUnreviewedCodeWarning,
				hasOpenReviewWarning,
			}) => (
				<IssueCardContainer
					href={issueUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onIssueClick}
					data-component-selector={ISSUE_CARD_DATA_COMPONENT_SELECTOR}
					// Warning: this test ID is used in Pollinator checks - do not change it without updating them.
					data-testid="software-releases-version-detail-issue-list.ui.issues.issue-card"
					hasWarnings={hasWarnings}
				>
					{jiraIssueSummaryField && jiraIssueTypeField && (
						<Summary
							jiraIssue={issue}
							jiraIssueSummaryField={jiraIssueSummaryField}
							jiraIssueTypeField={jiraIssueTypeField}
						/>
					)}
					{jiraPriorityField && !versionIssueTableHiddenColumns.includes('ISSUE_PRIORITY') && (
						<Priority jiraPriorityField={jiraPriorityField} />
					)}
					{jiraStatusField && !versionIssueTableHiddenColumns.includes('ISSUE_STATUS') && (
						<Status jiraStatusField={jiraStatusField} />
					)}
					{jiraAssigneeField && !versionIssueTableHiddenColumns.includes('ISSUE_ASSIGNEE') && (
						<Assignee jiraAssigneeField={jiraAssigneeField} />
					)}
					{!isDevopsFeatureDisabledInFedRamp() &&
						canViewDevTools &&
						hasDevelopments &&
						!versionIssueTableHiddenColumns.includes('DEVELOPMENT_STATUS') && (
							<ErrorBoundary
								id="issueListIssueCardDevelopment"
								fallback={() => <DevelopmentContainer />}
							>
								<Development jiraIssue={issue} onClick={handleClick} />
							</ErrorBoundary>
						)}
					{!isDevopsFeatureDisabledInFedRamp() &&
						(hasBambooDeployment || hasDeployments || hasBuilds) &&
						canViewDevTools &&
						!versionIssueTableHiddenColumns.includes('DEPLOYMENT_STATUS') && (
							<ErrorBoundary id="issueListIssueCardDelivery" fallback={() => <DeliveryContainer />}>
								<Delivery jiraIssue={issue} onClick={handleClick} />
							</ErrorBoundary>
						)}
					{!isDevopsFeatureDisabledInFedRamp() &&
						canViewDevTools &&
						devOpsSummarisedEntities &&
						!versionIssueTableHiddenColumns.includes('FEATURE_FLAG_STATUS') && (
							<ErrorBoundary
								id="issueListIssueCardFeatureFlags"
								fallback={() => <FeatureFlagsContainer />}
							>
								<div
									data-version-details-issue-click-ignore-target={ISSUE_CARD_CLICK_IGNORE_TARGET}
								>
									<FeatureFlags
										devOpsSummarisedEntities={devOpsSummarisedEntities}
										jiraIssue={issue}
										onClick={handleClick}
										featureFlagButtonRef={featureFlagButtonRef}
										setFeatureFlagEmptyStateModalOpen={onFeatureFlagEmptyStateModalOpen}
										rowNum={rowNum}
									/>
								</div>
							</ErrorBoundary>
						)}
					{!versionIssueTableHiddenColumns.includes('MORE_ACTION') && (
						<ErrorBoundary
							id="issueListIssueCardMoreButton"
							fallback={() => <OnHoverVisibility>{null}</OnHoverVisibility>}
						>
							<div data-version-details-issue-click-ignore-target={ISSUE_CARD_CLICK_IGNORE_TARGET}>
								<MoreButton
									jiraVersion={jiraVersionData}
									jiraIssue={issue}
									hasWarnings={hasWarnings}
									hasOpenPullRequestWarning={hasOpenPullRequestWarning}
									hasFailingBuildsWarning={hasFailingBuildsWarning}
									hasUnreviewedCodeWarning={hasUnreviewedCodeWarning}
									hasOpenReviewWarning={hasOpenReviewWarning}
								/>
							</div>
						</ErrorBoundary>
					)}
				</IssueCardContainer>
			)}
		</IssueWarningProvider>
	);
};

const WARNING_COLOR = token('color.background.warning');
const WARNING_HOVER_COLOR = token('color.background.warning.hovered');
const HOVER_COLOR = token('color.background.neutral.hovered');

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueCardContainer = styled.a<{ hasWarnings: boolean }>({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.hasWarnings && WARNING_COLOR,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover, :focus-within': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) => (props.hasWarnings ? WARNING_HOVER_COLOR : HOVER_COLOR),
	},

	height: '32px',
	borderWidth: `${token('border.width')} ${token('border.width')} 0px ${token('border.width')}`,
	borderStyle: 'solid',

	borderColor: `${token('color.border')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		borderBottomWidth: token('border.width'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderBottomLeftRadius: token('radius.small', '3px'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderBottomRightRadius: token('radius.small', '3px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:nth-of-type(1)': {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderTopLeftRadius: token('radius.small', '3px'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderTopRightRadius: token('radius.small', '3px'),
	},
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.200'),
	columnGap: token('space.100'),
	containerType: 'inline-size',
	'&:focus': {
		boxShadow: 'none',
		textDecoration: 'none',
	},
	'&:hover': {
		textDecoration: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FeatureFlagsContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (min-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		width: '104px',
		minWidth: '104px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (max-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		width: '24px',
		minWidth: '24px',
	},
});
